import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { Spinner } from "@/components/ui/spinner";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Banknote,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Flag,
  Receipt,
  Upload,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { BillSlipStatus, BillStatus, dayjs } from "@boo/shared";
import type { PublicBill, PublicBillItem } from "@boo/shared";
import { Button } from "@/components/ui/button";
import {
  BillClosedError,
  PUBLIC_API_BASE,
  confirmPublicShare,
  flagPublicItem,
  getPublicBill,
  publicQrUrl,
  publicReceiptUrl,
  setPublicClaim,
  reportPublicCash,
  submitPublicSlip,
  undoPublicShare,
} from "@/services/publicBillApi";
import { formatBaht } from "../lib/billMath";
import useCountUp from "../lib/useCountUp";
import { INPUT_CLASS } from "../types/bills.types";
import { downscaleImage } from "../lib/downscaleImage";
import { useBillEventStream } from "../lib/eventStream";
import LiveDot from "../components/LiveDot";
import UpdatesStrip from "./UpdatesStrip";
import NotifyCard from "./NotifyCard";
import BillProgress from "./BillProgress";
import Confetti from "./Confetti";
import PublicPersonAvatar from "./PublicPersonAvatar";

const storageKey = (token: string) => `boo-bill-person:${token}`;

function readStoredPerson(token: string): string | null {
  // `?p=` comes from reminder links and the installed home-screen icon, whose
  // storage is separate from the browser's.
  const fromLink = new URLSearchParams(window.location.search).get("p");
  if (fromLink) return fromLink;
  try {
    return localStorage.getItem(storageKey(token));
  } catch {
    return null;
  }
}

/**
 * Swap the app manifest for one that opens this exact bill as this friend, so
 * "Add to Home Screen" gives a per-bill icon. Absolute URLs: a blob manifest
 * has no base to resolve against.
 */
function usePerBillManifest(
  token: string,
  personId: string | null,
  title: string | undefined,
) {
  useEffect(() => {
    if (!personId || !title) return;
    const link = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]',
    );
    if (!link) return;
    const original = link.href;
    const origin = window.location.origin;
    const manifest = {
      name: title,
      short_name: title.slice(0, 12),
      start_url: `${origin}/b/${token}?p=${personId}`,
      scope: `${origin}/b/${token}`,
      display: "standalone",
      background_color: "#1A1A1A",
      theme_color: "#EF9F27",
      icons: [
        { src: `${origin}/icon-192.png`, sizes: "192x192", type: "image/png" },
        { src: `${origin}/icon-512.png`, sizes: "512x512", type: "image/png" },
      ],
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], {
        type: "application/manifest+json",
      }),
    );
    link.href = url;
    return () => {
      link.href = original;
      URL.revokeObjectURL(url);
    };
  }, [token, personId, title]);
}

function CountUp({ value }: { value: number }) {
  const ref = useCountUp(value, formatBaht);
  return <span ref={ref} className="inline-block rounded-lg" />;
}

function FlagForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (note: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-2 flex gap-2">
      <input
        autoFocus
        value={note}
        maxLength={140}
        placeholder="What's wrong? (e.g. price is 120)"
        onChange={(e) => setNote(e.target.value)}
        className={`${INPUT_CLASS} min-w-0 flex-1`}
      />
      <Button
        size="sm"
        disabled={busy || !note.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            await onSubmit(note.trim());
          } finally {
            setBusy(false);
          }
        }}
      >
        Send
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

export default function PublicBillPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [bill, setBill] = useState<PublicBill | null>(null);
  const [closed, setClosed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [personId, setPersonId] = useState<string | null>(() =>
    readStoredPerson(token),
  );
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [flagging, setFlagging] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [burst, setBurst] = useState<number | null>(null);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const prevBill = useRef<PublicBill | null>(null);
  const personRef = useRef(personId);
  useEffect(() => {
    personRef.current = personId;
  }, [personId]);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Single entry for every new copy of the bill: highlights rows that changed
  // (other friends' claims, payments) and celebrates our own payment.
  const applyBill = useCallback((next: PublicBill) => {
    const prev = prevBill.current;
    prevBill.current = next;
    setBill(next);
    if (!prev) return;
    const changed = new Set<string>();
    for (const item of next.items) {
      const old = prev.items.find((i) => i.id === item.id);
      if (old && old.claimedBy.join() !== item.claimedBy.join())
        changed.add(item.id);
    }
    for (const p of next.people) {
      const old = prev.people.find((o) => o.id === p.id);
      if (
        old &&
        (old.amount !== p.amount ||
          old.isPaid !== p.isPaid ||
          old.reviewed !== p.reviewed)
      )
        changed.add(p.id);
    }
    const wasPaid = prev.people.find((p) => p.id === personRef.current)?.isPaid;
    const isPaid = next.people.find((p) => p.id === personRef.current)?.isPaid;
    if (wasPaid === false && isPaid === true) setBurst(26);
    if (!changed.size) return;
    clearTimeout(flashTimer.current);
    setFlash(changed);
    flashTimer.current = setTimeout(() => setFlash(new Set()), 1300);
  }, []);

  const load = useCallback(async () => {
    try {
      applyBill(await getPublicBill(token));
      setFailed(false);
    } catch (err: unknown) {
      if (err instanceof BillClosedError) setClosed(true);
      else setFailed(true);
    }
  }, [token, applyBill]);

  useEffect(() => {
    // async load: state is set after the await, not synchronously
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Live updates: the owner's edits, other friends' claims and payments.
  const revision = useRef<string | null>(null);
  useEffect(() => {
    revision.current = bill?.revision ?? null;
  }, [bill]);
  const live = useBillEventStream(
    closed ? null : `${PUBLIC_API_BASE}/public/bills/${token}/events`,
    {
      auth: false,
      // Our own writes already returned the new copy, so equal revisions are skipped.
      onRevision: (r) => {
        if (r !== revision.current) void load();
      },
      onClosed: () => setClosed(true),
    },
  );

  usePerBillManifest(token, personId, bill?.title);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  function choose(id: string | null) {
    setPersonId(id);
    try {
      if (id) localStorage.setItem(storageKey(token), id);
      else localStorage.removeItem(storageKey(token));
    } catch {
      // private mode: the choice just isn't remembered
    }
  }

  if (closed) {
    return (
      <Shell>
        <p className="py-16 text-center text-sm text-muted-foreground">
          This bill is closed. Ask the person who shared it for a new link.
        </p>
      </Shell>
    );
  }
  if (failed && !bill) {
    return (
      <Shell>
        <p className="py-16 text-center text-sm text-muted-foreground">
          Could not load the bill.{" "}
          <button className="text-primary underline" onClick={load}>
            Retry
          </button>
        </p>
      </Shell>
    );
  }
  if (!bill) {
    return (
      <Shell>
        <SkeletonRegion className="space-y-4 py-6">
          <Skeleton className="h-8 w-2/3" />
          <ListRowsSkeleton rows={4} variant="cards" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-full" />
          </div>
        </SkeletonRegion>
      </Shell>
    );
  }

  const open = bill.status === BillStatus.Open;
  const friends = bill.people.filter((p) => !p.isOwner);
  const me = friends.find((p) => p.id === personId) ?? null;
  const names = new Map(bill.people.map((p) => [p.id, p.name]));
  const owner = bill.people.find((p) => p.isOwner);

  if (!me) {
    return (
      <Shell
        title={bill.title}
        subtitle={dayjs(bill.date).format("D MMM YYYY")}
        live={live}
      >
        <h2 className="mb-1 text-lg font-bold text-foreground">
          <span aria-hidden>👋 </span>Which one is you?
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {owner ? `${owner.name} shared this bill.` : "A bill was shared."}{" "}
          Pick your name to see your share.
        </p>
        <div className="flex flex-wrap gap-2">
          {friends.map((p, i) => (
            <button
              key={p.id}
              type="button"
              style={{ "--i": i } as React.CSSProperties}
              onClick={() => choose(p.id)}
              className="bill-fade-up inline-flex items-center gap-2 rounded-full border border-border bg-card py-2 pl-2.5 pr-4 text-sm font-medium text-foreground transition-transform hover:border-primary active:scale-[0.97]"
            >
              <PublicPersonAvatar token={token} person={p} size={24} />
              {p.name}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  async function toggle(item: PublicBillItem) {
    setBusyItem(item.id);
    try {
      applyBill(
        await setPublicClaim(
          token,
          me!.id,
          item.id,
          !item.claimedBy.includes(me!.id),
        ),
      );
    } catch (err) {
      if (err instanceof BillClosedError) setClosed(true);
      else toast.error(err instanceof Error ? err.message : "Failed");
      load();
    } finally {
      setBusyItem(null);
    }
  }

  async function toggleReview() {
    setReviewing(true);
    try {
      applyBill(
        await (me!.reviewed
          ? undoPublicShare(token, me!.id)
          : confirmPublicShare(token, me!.id)),
      );
      if (!me!.reviewed) {
        setBurst(10);
        toast.success("Thanks, the owner can see you're good 👍");
      }
    } catch (err) {
      if (err instanceof BillClosedError) setClosed(true);
      else toast.error(err instanceof Error ? err.message : "Failed");
      load();
    } finally {
      setReviewing(false);
    }
  }

  async function uploadSlip(file: File) {
    setUploading(true);
    try {
      const next = await submitPublicSlip(
        token,
        me!.id,
        await downscaleImage(file),
      );
      applyBill(next);
      const status = next.people.find((p) => p.id === me!.id)?.slipStatus;
      if (status === BillSlipStatus.Verified)
        toast.success("Payment confirmed");
      else toast.message("Slip sent, the owner will confirm it");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function reportCash() {
    setUploading(true);
    try {
      applyBill(await reportPublicCash(token, me!.id));
      toast.message("Sent, the owner will confirm it");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send");
    } finally {
      setUploading(false);
    }
  }

  async function sendFlag(item: PublicBillItem, note: string) {
    try {
      applyBill(await flagPublicItem(token, me!.id, item.id, note));
      setFlagging(null);
      toast.success("Sent to the owner");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  }

  const hasClaim =
    bill.goDutch || bill.items.some((i) => i.claimedBy.includes(me.id));
  const hasFlag = bill.items.some((i) => i.flaggedBy.includes(me.id));
  const doneCount = friends.filter((p) =>
    open ? p.reviewed : p.isPaid,
  ).length;
  const allConfirmed =
    open && friends.length > 0 && friends.every((p) => p.reviewed);

  function statusChip(p: (typeof friends)[number]) {
    if (open) return p.reviewed ? "✅ Confirmed" : "🤔 Choosing";
    if (p.isPaid) return "✅ Paid";
    if (p.slipStatus === BillSlipStatus.Pending) return "⏳ Checking";
    return "💸 To pay";
  }

  const itemsBlock = (
    <>
      {bill.goDutch && (
        <p className="mt-4 rounded-xl border border-border bg-card p-3 text-sm text-foreground">
          หารเท่า 🍰 The total is split equally, no need to pick items.
        </p>
      )}
      <ul className="mt-4 space-y-2">
        {bill.items.map((item) => {
          const mine = !bill.goDutch && item.claimedBy.includes(me.id);
          const flagged = item.flaggedBy.includes(me.id);
          return (
            <li
              key={item.id}
              className={`rounded-xl border bg-card p-3 ${
                mine ? "border-primary" : "border-border"
              } ${flash.has(item.id) ? "bill-row-flash" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {item.name}
                    {item.quantity > 1 && (
                      <span className="text-muted-foreground">
                        {" "}
                        ×{item.quantity}
                      </span>
                    )}
                  </div>
                  {!bill.goDutch && (
                    <div className="mt-1 flex flex-wrap gap-1 text-xs">
                      {item.claimedBy.length ? (
                        item.claimedBy.map((id) => (
                          <span
                            key={id}
                            className={`bill-pop rounded-full px-2 py-0.5 ${
                              id === me.id
                                ? "bg-primary/15 font-semibold text-primary"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {names.get(id)}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted-foreground">
                          🍽️ Nobody yet
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-sm font-semibold text-foreground [font-variant-numeric:tabular-nums]">
                  {formatBaht(item.amount)}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                {open && !bill.goDutch && (
                  <Button
                    key={mine ? "mine" : "not"}
                    size="sm"
                    className={mine ? "bill-pop" : undefined}
                    variant={mine ? "default" : "outline"}
                    disabled={busyItem === item.id}
                    onClick={() => toggle(item)}
                  >
                    {mine ? "I had this ✓" : "I had this"}
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setFlagging(flagging === item.id ? null : item.id)
                  }
                  className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                >
                  <Flag className="w-3.5 h-3.5" />
                  {flagged ? "Flagged" : "Looks wrong"}
                </button>
              </div>
              {flagging === item.id && (
                <FlagForm
                  onSubmit={(note) => sendFlag(item, note)}
                  onCancel={() => setFlagging(null)}
                />
              )}
            </li>
          );
        })}
      </ul>

      {bill.charges.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm">
          {bill.charges.map((c, i) => (
            <li key={i} className="flex justify-between text-muted-foreground">
              <span>{c.label}</span>
              <span className="[font-variant-numeric:tabular-nums]">
                {formatBaht(c.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {bill.hasReceipt && (
        <a
          href={publicReceiptUrl(token)}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary"
        >
          <Receipt className="w-4 h-4" /> View receipt
        </a>
      )}
    </>
  );
  const payBlock = (
    <>
      {!open && bill.payment && (
        <section className="mt-6 rounded-xl border border-border bg-card p-4 text-center">
          <div className="text-sm font-semibold text-foreground">
            <span aria-hidden>💸 </span>Pay {formatBaht(me.amount)}
          </div>
          {bill.payment.hasQr && (
            <img
              src={publicQrUrl(token)}
              alt="Payment QR"
              className="mx-auto mt-3 max-h-72 object-contain"
            />
          )}
          <div className="mt-3 space-y-0.5 text-sm">
            {bill.payment.bankName && (
              <div className="text-muted-foreground">
                {bill.payment.bankName}
              </div>
            )}
            {bill.payment.accountNumber && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(bill.payment!.accountNumber!);
                  toast.success("Account number copied");
                }}
                className="inline-flex items-center gap-1.5 text-lg font-semibold text-foreground [font-variant-numeric:tabular-nums]"
              >
                {bill.payment.accountNumber}
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            {bill.payment.accountHolderName && (
              <div className="text-muted-foreground">
                {bill.payment.accountHolderName}
              </div>
            )}
          </div>
          {!me.isPaid && (
            <div className="mt-4 border-t border-border pt-4">
              {me.slipStatus === BillSlipStatus.Pending && (
                <p className="mb-2 text-xs text-muted-foreground">
                  Payment reported. Waiting for the owner to confirm.
                </p>
              )}
              {me.slipStatus === BillSlipStatus.Rejected && (
                <p className="mb-2 text-xs text-destructive">
                  😬 The owner rejected your slip. Please upload it again.
                </p>
              )}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:border-primary">
                {uploading ? (
                  <Spinner className="size-4" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {uploading ? "Checking slip…" : "I've paid, upload slip"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) uploadSlip(file);
                  }}
                />
              </label>
              {me.slipStatus !== BillSlipStatus.Pending && (
                <button
                  type="button"
                  disabled={uploading}
                  onClick={reportCash}
                  className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:border-primary disabled:opacity-50"
                >
                  <Banknote className="w-4 h-4" />I paid cash
                </button>
              )}
            </div>
          )}
        </section>
      )}
    </>
  );
  const everyoneBlock = (
    <>
      <section className="mt-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground">
            Everyone
          </h2>
          <span className="text-xs text-muted-foreground">
            {doneCount} of {friends.length} {open ? "confirmed" : "paid"}
          </span>
        </div>
        <div className="mb-3 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full origin-left rounded-full bg-primary transition-transform duration-500"
            style={{
              transform: `scaleX(${friends.length ? doneCount / friends.length : 0})`,
            }}
          />
        </div>
        <ul className="space-y-1 text-base">
          {friends.map((p) => (
            <li
              key={p.id}
              className={`flex items-center justify-between rounded-lg px-2 py-1 text-foreground ${
                flash.has(p.id) ? "bill-row-flash" : ""
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <PublicPersonAvatar token={token} person={p} size={24} />
                {p.name}
                <span className="text-xs text-muted-foreground">
                  {statusChip(p)}
                </span>
              </span>
              <span className="font-medium [font-variant-numeric:tabular-nums]">
                {formatBaht(p.amount)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );

  return (
    <Shell
      title={bill.title}
      subtitle={`${dayjs(bill.date).format("D MMM YYYY")}${
        owner ? ` · paid by ${owner.name}` : ""
      }`}
      live={live}
    >
      <UpdatesStrip token={token} personId={me.id} revision={bill.revision} />
      <NotifyCard token={token} personId={me.id} />
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-base text-muted-foreground">
          <PublicPersonAvatar token={token} person={me} size={28} />
          <span>
            {me.name}
            {open ? ", you owe so far" : ", you owe"}
          </span>
        </div>
        <div className="text-4xl font-bold text-foreground [font-variant-numeric:tabular-nums]">
          <CountUp value={me.amount} />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          {!open && me.isPaid ? (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1.5 text-sm font-semibold text-primary">
              <Check className="w-4 h-4" /> Paid 🎉
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => choose(null)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:border-primary"
          >
            <UserRound className="w-4 h-4" />
            Not {me.name}?
          </button>
        </div>
      </div>

      <BillProgress open={open} me={me} hasClaim={hasClaim} hasFlag={hasFlag} />

      {allConfirmed && (
        <p className="bill-fade-up mt-3 text-center text-sm font-medium text-primary">
          🎊 Everyone confirmed. Waiting for the owner to finalise
        </p>
      )}

      {open && hasClaim && (
        <div className="mt-3">
          {me.reviewed ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-primary bg-primary/10 px-4 py-2.5 text-sm text-primary">
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4" /> You confirmed{" "}
                {formatBaht(me.amount)} 👍
              </span>
              <button
                type="button"
                disabled={reviewing}
                onClick={toggleReview}
                className="text-xs underline"
              >
                Undo
              </button>
            </div>
          ) : (
            <>
              <Button
                className="w-full"
                disabled={reviewing || hasFlag}
                onClick={toggleReview}
              >
                My share {formatBaht(me.amount)} is correct
              </Button>
              {hasFlag && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Waiting for the owner to look at the line you flagged.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {burst !== null && (
        <Confetti count={burst} onDone={() => setBurst(null)} />
      )}

      {open && (
        <p className="mt-4 text-xs text-muted-foreground">
          Tap the items you had. Shared items are split between everyone who
          taps them. The QR appears once the owner finalises.
        </p>
      )}
      {open ? (
        <>
          {itemsBlock}
          {everyoneBlock}
        </>
      ) : (
        <>
          {payBlock}
          {everyoneBlock}
          <button
            type="button"
            onClick={() => setShowItems((v) => !v)}
            className="mt-6 flex w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-foreground"
          >
            <span>Items &amp; receipt ({bill.items.length})</span>
            {showItems ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          {showItems && itemsBlock}
        </>
      )}
    </Shell>
  );
}

function Shell({
  title,
  subtitle,
  live,
  children,
}: {
  title?: string;
  subtitle?: string;
  live?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <div className="mx-auto max-w-[520px] px-4 pb-16 pt-6">
        {title && (
          <header className="mb-4">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-xl font-bold text-foreground">{title}</h1>
              {live !== undefined && <LiveDot live={live} />}
            </div>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </header>
        )}
        {children}
      </div>
    </div>
  );
}
